import {
  scopedProjectKey,
  scopedThreadKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@afar-research/client-runtime/environment";
import type {
  ThreadDiscoveryProject,
  ThreadDiscoveryThread,
} from "@afar-research/client-runtime/state/thread-discovery";
import type { EnvironmentThreadShell } from "@afar-research/client-runtime/state/shell";
import { EnvironmentId, ProjectId, ProviderInstanceId, ThreadId } from "@afar-research/contracts";
import * as Schema from "effect/Schema";

const THREAD_DISCOVERY_CACHE_KEY = "afar-research:thread-discovery-cache:v1";
const THREAD_DISCOVERY_CACHE_LIMIT = 2_000;

const CachedProject = Schema.Struct({
  environmentId: EnvironmentId,
  id: ProjectId,
  title: Schema.String,
  workspaceRoot: Schema.String,
});
const CachedThread = Schema.Struct({
  archivedAt: Schema.NullOr(Schema.String),
  branch: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  environmentId: EnvironmentId,
  id: ThreadId,
  latestUserMessageAt: Schema.NullOr(Schema.String),
  projectId: ProjectId,
  title: Schema.String,
  updatedAt: Schema.String,
});
const ThreadDiscoveryCache = Schema.Struct({
  projects: Schema.Array(CachedProject),
  threads: Schema.Array(CachedThread).check(Schema.isMaxLength(THREAD_DISCOVERY_CACHE_LIMIT)),
});
const decodeThreadDiscoveryCache = Schema.decodeUnknownSync(ThreadDiscoveryCache);

export interface ThreadDiscoveryCacheRows {
  readonly projects: ReadonlyArray<ThreadDiscoveryProject>;
  readonly threads: ReadonlyArray<ThreadDiscoveryThread>;
}

export interface ThreadDiscoveryCacheWriteRows extends ThreadDiscoveryCacheRows {
  readonly pinnedThreadRefs?: ReadonlyArray<{
    readonly environmentId: EnvironmentId;
    readonly threadId: ThreadId;
  }>;
}

export function hydrateCachedThreadDiscoveryShell(
  thread: ThreadDiscoveryThread,
): EnvironmentThreadShell {
  return {
    ...thread,
    hasActionableProposedPlan: false,
    hasPendingApprovals: false,
    hasPendingUserInput: false,
    interactionMode: "default",
    latestTurn: null,
    latestUserMessageAt: thread.latestUserMessageAt ?? null,
    modelSelection: {
      instanceId: ProviderInstanceId.make("codex"),
      model: "unknown",
    },
    runtimeMode: "full-access",
    session: null,
    worktreePath: null,
  };
}

export function readThreadDiscoveryCache(
  storage: Pick<Storage, "getItem">,
): ThreadDiscoveryCacheRows {
  try {
    const raw = storage.getItem(THREAD_DISCOVERY_CACHE_KEY);
    if (raw === null) return { projects: [], threads: [] };
    return decodeThreadDiscoveryCache(JSON.parse(raw));
  } catch {
    return { projects: [], threads: [] };
  }
}

export function writeThreadDiscoveryCache(
  storage: Pick<Storage, "setItem">,
  rows: ThreadDiscoveryCacheWriteRows,
  previousSerialized: string | null = null,
): string {
  const liveThreadKeys = new Set(
    rows.threads.map((thread) =>
      scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId)),
    ),
  );
  const pinnedThreadKeys = new Set((rows.pinnedThreadRefs ?? []).map(scopedThreadKey));
  const threads = rows.threads
    .filter((thread) => thread.archivedAt === null)
    .toSorted((left, right) => {
      const leftPinned = pinnedThreadKeys.has(
        scopedThreadKey(scopeThreadRef(left.environmentId, left.id)),
      );
      const rightPinned = pinnedThreadKeys.has(
        scopedThreadKey(scopeThreadRef(right.environmentId, right.id)),
      );
      return (
        Number(rightPinned) - Number(leftPinned) ||
        Date.parse(right.updatedAt) - Date.parse(left.updatedAt)
      );
    })
    .slice(0, THREAD_DISCOVERY_CACHE_LIMIT)
    .map((thread) => ({
      archivedAt: thread.archivedAt,
      branch: thread.branch,
      createdAt: thread.createdAt,
      environmentId: thread.environmentId,
      id: thread.id,
      latestUserMessageAt: thread.latestUserMessageAt ?? null,
      projectId: thread.projectId,
      title: thread.title,
      updatedAt: thread.updatedAt,
    }));
  const retainedProjectKeys = new Set(
    threads.map((thread) =>
      scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId)),
    ),
  );
  const serialized = JSON.stringify({
    projects: rows.projects
      .filter((project) =>
        liveThreadKeys.has(scopedProjectKey(scopeProjectRef(project.environmentId, project.id))),
      )
      .filter((project) =>
        retainedProjectKeys.has(
          scopedProjectKey(scopeProjectRef(project.environmentId, project.id)),
        ),
      )
      .map((project) => ({
        environmentId: project.environmentId,
        id: project.id,
        title: project.title,
        workspaceRoot: project.workspaceRoot,
      })),
    threads,
  });
  if (serialized !== previousSerialized) {
    storage.setItem(THREAD_DISCOVERY_CACHE_KEY, serialized);
  }
  return serialized;
}
